import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { PulseDot } from "./PulseDot";
import { BAND } from "./pulse";

// Pulse on a community card: a real on/off switch (administrators + Redline staff) next to the live
// status. On → colour band + score, tap opens that community's Pulse. Off → "Pulse off" + switch.
// Not in the plan → "ask Redline" (switch disabled). `snap` comes from GET /pulse/badge.
export function PulseChip({ tenant, snap, role, onOpen, onTurnedOn }) {
  const [busy, setBusy] = useState(false);
  const entitled = !!tenant?.entitlements?.pulse;
  const on = entitled && !!tenant?.pulse_enabled;
  const platform = role === "super_admin" || role === "platform_admin";
  const canToggle = platform || (role === "company_admin" && entitled);
  if (role === "board_member" || role === "hoa_manager") return null;

  const flip = async (next) => {
    setBusy(true);
    try {
      const r = await api.post(`/tenants/${tenant.id}/community-pulse/${next ? "turn-on" : "turn-off"}`);
      toast.success(next
        ? `Pulse is on for ${tenant.name}${r.data?.snapshot?.score != null ? ` — ${r.data.snapshot.score} today` : ""}`
        : `Pulse is off for ${tenant.name}`);
      onTurnedOn?.(r.data);
    } catch (err) { toast.error(err?.response?.data?.detail || `Couldn't turn Pulse ${next ? "on" : "off"}`); }
    finally { setBusy(false); }
  };

  const band = snap ? (snap.band || "quiet") : "quiet";
  const b = BAND[band] || BAND.quiet;
  const d = snap?.delta14;
  const status = on ? (
    <button type="button" data-testid={`pulse-chip-${tenant.id}`} onClick={(e) => { e.stopPropagation(); onOpen?.(); }} title="Open this community's Pulse"
      className={`inline-flex items-center gap-1.5 rounded-full border border-sand-200 hover:border-ink px-2.5 py-1 text-xs whitespace-nowrap transition-colors ${b.soft}`}>
      <PulseDot band={band} quiet={!!snap?.expected} size={9} />
      <span className={`font-extrabold uppercase tracking-[0.04em] text-[10px] ${b.text}`}>{snap ? b.word : "Pulse"}</span>
      {snap?.score != null && <span className="font-mono font-bold text-ink tabular-nums">{snap.score}</span>}
      {typeof d === "number" && d !== 0 && <span className={`font-bold ${d > 0 ? "text-[#15803D]" : "text-[#B91C1C]"}`}>{d > 0 ? "▲" : "▼"}{Math.abs(d)}</span>}
      {!snap && <span className="text-ink/45">first score tomorrow</span>}
    </button>
  ) : (
    <span data-testid={`pulse-chip-off-${tenant.id}`} className="text-xs font-semibold text-ink/45 whitespace-nowrap"
      title={entitled || platform ? "Community Pulse is off for this community" : "Pulse isn't in this community's plan yet — ask Redline to add it"}>
      {entitled || platform ? "Pulse off" : "Pulse · ask Redline"}
    </span>
  );
  if (!canToggle && !on) return status;
  return (
    <span className="inline-flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
      {status}
      {canToggle && (
        <Switch data-testid={`pulse-switch-${tenant.id}`} checked={on} disabled={busy} onCheckedChange={flip}
          aria-label={on ? "Turn Pulse off" : "Turn Pulse on"} title={on ? "Turn Pulse off" : "Turn Pulse on"}
          className="data-[state=checked]:bg-[#15803D] data-[state=unchecked]:bg-sand-300" />
      )}
    </span>
  );
}
